import { useState, useEffect, useRef } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useTranslation, Trans } from 'react-i18next'
import { register as apiRegister } from '../api/auth'
import { getFingerprint } from '../hooks/useFingerprint'
import {Coin} from "../icons/Coin.tsx";
import { rememberReturn, authUrl, referrerPath, safeReturnPath } from '../utils/authReturn'
import { useAuthNav } from '../hooks/useAuthModal'
import { useWelcomeCredits } from '../hooks/useWelcomeCredits'
import { useTurnstile } from '../hooks/useTurnstile'
import TurnstileWidget, { type TurnstileWidgetHandle } from '../components/TurnstileWidget'

const TG_BOT_ID = '8464718685'

export default function Register() {
  const { t } = useTranslation()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)
  const [agreed, setAgreed] = useState(false)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [touched, setTouched] = useState<Record<string, boolean>>({})
  // Регистрация — всегда (TZ_turnstile_captcha.md, 3.4): виджет рендерится
  // сразу, кнопка ждёт токен.
  const [captchaToken, setCaptchaToken] = useState('')
  const captchaWidgetRef = useRef<TurnstileWidgetHandle>(null)
  const { enabled: captchaEnabled } = useTurnstile()
  const welcomeCredits = useWelcomeCredits()
  // Переходы к «Подтвердите email» и ко входу: в окне поверх страницы держат её под окном (useAuthNav)
  const authNav = useAuthNav()
  const [searchParams] = useSearchParams()

  // Запоминаем «откуда пришёл» (?next) — вернёмся туда после верификации/входа.
  useEffect(() => {
    rememberReturn(searchParams.get('next') || referrerPath())
  }, [searchParams])

  const isEmailValid = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email)
  const isEmailLatin = /^[a-zA-Z0-9._%+@-]*$/.test(email)
  const isPasswordValid = password.length >= 6
  const isConfirmValid = confirmPassword.length > 0 && password === confirmPassword
  const isConfirmError = touched.confirm && confirmPassword.length > 0 && password !== confirmPassword

  useEffect(() => {
    const ref = searchParams.get('ref')
    if (ref) {
      localStorage.setItem('referral_code', ref)
    }
  }, [searchParams])

  const getEmailState = () => {
    if (!touched.email || !email) return ''
    if (!isEmailLatin) return 'error'
    if (isEmailValid) return 'valid'
    return ''
  }

  const getPasswordState = () => {
    if (!touched.password || !password) return ''
    if (isPasswordValid) return 'valid'
    return ''
  }

  const getConfirmState = () => {
    if (!touched.confirm || !confirmPassword) return ''
    if (isConfirmValid) return 'valid'
    if (isConfirmError) return 'error'
    return ''
  }

  const getEmailError = () => {
    if (!touched.email || !email) return ''
    if (!isEmailLatin) return t('auth.error.emailLatin')
    return ''
  }

  const getConfirmError = () => {
    if (isConfirmError) return t('auth.error.passwordMismatchField')
    return ''
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setTouched({ email: true, password: true, confirm: true })

    if (!isEmailValid) {
      setError(!isEmailLatin ? t('auth.error.emailLatinShort') : t('auth.error.emailInvalidShort'))
      return
    }

    if (!isPasswordValid) {
      setError(t('auth.error.passwordMin'))
      return
    }

    if (password !== confirmPassword) {
      setError(t('auth.error.passwordMismatch'))
      return
    }

    if (!agreed) {
      setError(t('auth.error.agreeRequired'))
      return
    }

    if (captchaEnabled && !captchaToken) {
      setError(t('auth.captcha.required'))
      return
    }

    setLoading(true)
    try {
      const referralCode = localStorage.getItem('referral_code') || undefined
      let fingerprint: string | undefined
      try {
        fingerprint = await getFingerprint()
      } catch {
        fingerprint = undefined
      }

      // next для возврата после подтверждения email — уйдёт в письмо (когда бэк готов).
      const result = await apiRegister(email, password, '', referralCode, fingerprint, safeReturnPath(searchParams.get('next') || referrerPath()), captchaToken || undefined)
      if (result.ok) {
        localStorage.removeItem('referral_code')
        authNav.go(`/verify-pending?email=${encodeURIComponent(email)}`)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('auth.error.registerFailed'))
      // Токен одноразовый — сброс после любой неудачи.
      captchaWidgetRef.current?.reset()
      setCaptchaToken('')
    } finally {
      setLoading(false)
    }
  }

  const handleTelegram = () => {
    const origin = encodeURIComponent(window.location.origin)
    const returnTo = encodeURIComponent('https://umnik.ai/auth/telegram-callback')
    window.location.href = `https://oauth.telegram.org/auth?bot_id=${TG_BOT_ID}&origin=${origin}&embed=0&request_access=write&return_to=${returnTo}`
  }

  const emailState = getEmailState()
  const passwordState = getPasswordState()
  const confirmState = getConfirmState()

  return (
    <div className="auth-page">
      <div className="auth-card-frame gradient-frame">
      <div className="auth-card">
        {/* Бонус выключен (0) или ещё грузится (null) — бейджа нет */}
        {!!welcomeCredits && (
          <span className="auth-card__badge">
            {welcomeCredits} <Coin className="auth-card__icon" /> {t('auth.registrationBonus')}
          </span>
        )}
        <h1 className="auth-title">{t('auth.register.title')}</h1>
        <p className="auth-subtitle">{t('auth.register.subtitle')}</p>

        {error && <div className="auth-error">{error}</div>}

        <form onSubmit={handleSubmit} className="auth-form" noValidate>
          <div className="auth-input-wrapper">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onBlur={() => setTouched(t => ({ ...t, email: true }))}
              placeholder="E-mail"
              className={`auth-input ${emailState}`}
              required
            />
            {emailState === 'valid' && <img src="/icons/check.svg" className="auth-input-icon" alt="" width="18" height="16" />}
            {emailState === 'error' && <img src="/icons/cross.svg" className="auth-input-icon" alt="" width="10" height="10" />}
          </div>
          {getEmailError() && <div className="auth-field-error">{getEmailError()}</div>}

          <div className="auth-input-wrapper">
            <input
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onBlur={() => setTouched(t => ({ ...t, password: true }))}
              placeholder={t('auth.password')}
              className={`auth-input ${passwordState}`}
              required
            />
            {password.length > 0 && (
              <button type="button" className="auth-eye-btn" onClick={() => setShowPassword(!showPassword)}>
                <img src={showPassword ? '/icons/visible.svg' : '/icons/notvisible.svg'} alt="" width="15" />
              </button>
            )}
          </div>

          <div className="auth-input-wrapper">
            <input
              type={showConfirm ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              onBlur={() => setTouched(t => ({ ...t, confirm: true }))}
              placeholder={t('auth.confirmPassword')}
              className={`auth-input ${confirmState}`}
              required
            />
            {confirmState === 'error' ? (
              <img src="/icons/cross.svg" className="auth-input-icon" alt="" width="10" height="10" />
            ) : confirmPassword.length > 0 ? (
              <button type="button" className="auth-eye-btn" onClick={() => setShowConfirm(!showConfirm)}>
                <img src={showConfirm ? '/icons/visible.svg' : '/icons/notvisible.svg'} alt="" width="15" />
              </button>
            ) : null}
          </div>
          {getConfirmError() && <div className="auth-field-error">{getConfirmError()}</div>}

          <label className="auth-checkbox">
            <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} />
            <span><Trans i18nKey="auth.agreeTerms" components={{ privacy: <a href="/terms" target="_blank" />, terms: <a href="/terms" target="_blank" /> }} /></span>
          </label>

          <TurnstileWidget
            ref={captchaWidgetRef}
            action="register"
            onToken={setCaptchaToken}
            onExpire={() => setCaptchaToken('')}
          />

          <button
            type="submit"
            className="auth-btn auth-btn-primary"
            disabled={loading || !agreed || (captchaEnabled && !captchaToken)}
          >
            {loading ? t('auth.register.loading') : t('auth.register.submit')}
          </button>
        </form>

        <button className="auth-btn auth-btn-telegram" onClick={handleTelegram}>
          {t('auth.telegramLogin')}
          <img src="/icons/telegram.svg" alt="" width="20" height="20" />
        </button>

        <Link to={authUrl('/login', searchParams.get('next'))} {...authNav.link} className="auth-link">{t('auth.hasAccount')}</Link>
      </div>
      </div>
    </div>
  )
}
